import { useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { CLASS_LABEL } from "@/components/FareBreakdown";

const TITLES = {
  book_ride: "Book a ride", cancel_trip: "Cancel your trip", apply_promo: "Apply promo code", open_ticket: "Open a support ticket",
  driver_pause: "Pause (no new requests)", driver_resume: "Resume — go online", driver_stop: "Stop — go offline",
};

const geo = () => new Promise((res, rej) => navigator.geolocation
  ? navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => rej(new Error("Location access required to go online")), { timeout: 15000 })
  : rej(new Error("Location access required to go online")));

async function execute(a) {
  const d = a.details || {};
  switch (a.type) {
    case "book_ride": {
      const { data } = await api.post("/trips", { pickup: d.pickup, stops: d.stops, vehicle_class: d.vehicle_class, promo_code: d.promo_code, requested_driver_code: d.requested_driver?.driver_code || null, origin_url: window.location.origin });
      return { note: "Redirecting to secure payment", redirect: data.checkout_url };
    }
    case "cancel_trip": {
      const { data } = await api.post(`/trips/${d.trip_id}/cancel`);
      return { note: data.cancel?.fee > 0 ? `Cancelled — ${money(data.cancel.fee)} fee kept, ${money(data.cancel.refund)} refunded` : "Cancelled — full refund" };
    }
    case "apply_promo":
      sessionStorage.setItem("lux_promo", d.code);
      window.dispatchEvent(new CustomEvent("lux-promo", { detail: d.code }));
      return { note: `${d.code} will be applied to your next booking estimate` };
    case "open_ticket": {
      await api.post("/tickets", { subject: d.subject, message: d.message });
      return { note: "Ticket opened in Customer Service" };
    }
    case "driver_pause": await api.post("/driver/pause"); return { note: "You're paused" };
    case "driver_resume": await api.post("/driver/online", await geo()); return { note: "You're online" };
    case "driver_stop": await api.post("/driver/offline"); return { note: "You're offline" };
    default: throw new Error("Unsupported action");
  }
}

const Details = ({ a }) => {
  const d = a.details || {};
  if (a.type === "book_ride") return (
    <div className="space-y-1 text-xs text-zinc-300">
      <p><span className="text-amber-400">P</span> {d.pickup?.address}</p>
      <p><span className="text-amber-400">1</span> {d.stops?.[0]?.address}</p>
      <p data-testid="ai-action-estimate" className="pt-1 text-amber-100">{CLASS_LABEL[d.vehicle_class]} · {d.fare?.miles} mi · {d.fare?.minutes} min · <strong>{money(d.fare?.total)}</strong>{d.promo_code && ` · ${d.promo_code}`}</p>
      {d.requested_driver && <p data-testid="ai-action-requested-driver">Requested driver: {d.requested_driver.first_name} ({d.requested_driver.driver_code})</p>}
      <p className="text-zinc-500">You'll pay on the secure card payment page; a driver is requested only after payment.</p>
    </div>
  );
  if (a.type === "cancel_trip") return <p data-testid="ai-action-cancel-fee" className={`text-xs ${d.fee > 0 ? "text-amber-300" : "text-zinc-300"}`}>{d.fee > 0 ? `${d.message}. Refund ${money(d.refund)}.` : `No cancel fee — refund ${money(d.refund)}.`}</p>;
  if (a.type === "apply_promo") return <p className="font-mono text-xs text-amber-300">{d.code}</p>;
  if (a.type === "open_ticket") return <p className="text-xs text-zinc-300">{d.subject}</p>;
  return null;
};

export const ActionCard = ({ action }) => {
  const [status, setStatus] = useState(action.status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const resolve = (s, n = "") => api.post(`/ai/actions/${action.id}`, { status: s, note: n }).catch(() => {});
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await execute(action);
      await resolve("confirmed", r.note);
      setStatus("confirmed");
      setNote(r.note);
      if (r.redirect) window.location.href = r.redirect;
    } catch (e) {
      const m = e.response ? errMsg(e) : e.message;
      await resolve("failed", m);
      setStatus("failed");
      setNote(m);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => { await resolve("cancelled"); setStatus("cancelled"); };
  return (
    <div data-testid={`ai-action-card-${action.type}`} className="mt-2 rounded-xl border border-amber-400/40 bg-[#0a0a0c] p-3">
      <p className="lux-eyebrow mb-2">{TITLES[action.type]}</p>
      <Details a={action} />
      {status === "proposed" ? (
        <div className="mt-3 flex gap-2">
          <button data-testid="ai-action-confirm-button" disabled={busy} onClick={confirm} className="btn-gold !px-4 !py-2 !text-xs">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Confirm</button>
          <button data-testid="ai-action-cancel-button" disabled={busy} onClick={cancel} className="btn-outline-gold !px-4 !py-2 !text-xs"><X className="h-3 w-3" /> Cancel</button>
        </div>
      ) : (
        <p data-testid="ai-action-status" className={`mt-2 text-xs ${status === "confirmed" ? "text-emerald-300" : status === "failed" ? "text-red-300" : "text-zinc-500"}`}>
          {status === "confirmed" ? "Confirmed" : status === "failed" ? "Failed" : "Cancelled — nothing was changed"}{note && ` · ${note}`}
        </p>
      )}
    </div>
  );
};
